import React from "react";
import type { ClassSession } from "../../../interfaces/classSessionsDetail";
import { useLocalization } from "../../../stores/localizationContext";
import { formatSessionDate, formatSessionTimeRange, formatDuration } from "../../../utils";
import "./TutorSessionCard.scss";

interface TutorSessionCardProps {
  session: ClassSession;
  currentServerDate: string;
  onStart?: () => void;
  onPreview?: () => void;
  onEndedClick?: () => void;
}

const TutorSessionCard: React.FC<TutorSessionCardProps> = ({
  session,
  currentServerDate,
  onStart,
  onPreview,
  onEndedClick,
}) => {
  const { t, lang } = useLocalization();

  const isEnded = session.status === "Ended";
  const isInProgress = session.status === "In Progress";
  // isInProgress is used for the Start/Join label only

  // Session is startable when server time (+ buffer) has reached the session start time
  // and the session is not ended yet
  const bufferMinutes = Number(import.meta.env.VITE_BUFFER_START_SESSION ?? 0);
  const now = new Date(currentServerDate).getTime();
  const sessionStart = new Date(session.session_start_date).getTime();
  const isStartable = isInProgress || (!isEnded && now + bufferMinutes * 60 * 1000 >= sessionStart);

  const locale = lang === "ar" ? "ar-EG" : "en-US";

  const formattedDate = formatSessionDate(session.session_start_date, locale);
  const formattedTimeRange = formatSessionTimeRange(
    session.session_start_date,
    session.session_end_date,
    locale
  );
  const formattedDuration = formatDuration(session.session_duration_in_minutes, t);

  const startLabel = isInProgress
    ? t("join_button")
    : t("start_label");

  // Ended session — compact row with chevron, clickable
  if (isEnded) {
    return (
      <div
        className="tutor-session-card tutor-session-card--ended"
        dir="ltr"
        onClick={onEndedClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onEndedClick?.()}
      >
        <div className="tutor-session-card__info">
          <p className="tutor-session-card__title">{session.session_title}</p>
          <p className="tutor-session-card__date">{formattedDate}</p>
        </div>
        <i className="f-icon chevron-right-icon tutor-session-card__chevron" />
      </div>
    );
  }

  // Startable session (time has arrived) — blue card, active buttons
  if (isStartable) {
    return (
      <div className="tutor-session-card tutor-session-card--in-progress" dir="ltr">
        <div className="tutor-session-card__top">
          <p className="tutor-session-card__title">{session.session_title}</p>
        </div>
        <hr className="tutor-session-card__divider" />
        <p className="tutor-session-card__datetime">{formattedTimeRange}</p>
        <div className="tutor-session-card__footer">
          <div className="tutor-session-card__duration">
            <i className="f-icon fa-light stopwatch-icon" />
            <span>{formattedDuration}</span>
          </div>
          <div className="tutor-session-card__actions">
            <span className="tutor-session-card__btn tutor-session-card__btn--outline-white tutor-session-card__tag">
              {t("ai_session", "AI Session")}
            </span>
            <button
              className="tutor-session-card__btn tutor-session-card__btn--outline-white"
              onClick={onPreview}
            >
              {t("preview_label")}
            </button>
            <button
              className="tutor-session-card__btn tutor-session-card__btn--solid-white"
              onClick={onStart}
            >
              {startLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Upcoming session — time not yet reached, grey card, dimmed unclickable Start
  return (
    <div className="tutor-session-card tutor-session-card--upcoming" dir="ltr">
      <div className="tutor-session-card__top">
        <p className="tutor-session-card__title">{session.session_title}</p>
      </div>
      <hr className="tutor-session-card__divider" />
      <p className="tutor-session-card__datetime">{formattedTimeRange}</p>
      <div className="tutor-session-card__footer">
        <div className="tutor-session-card__duration">
          <i className="f-icon fa-light stopwatch-icon" />
          <span>{formattedDuration}</span>
        </div>
          <div className="tutor-session-card__actions">
            <span className="tutor-session-card__btn tutor-session-card__btn--outline-dark tutor-session-card__tag">
              {t("ai_session", "AI Session")}
            </span>
            <button
              className="tutor-session-card__btn tutor-session-card__btn--outline-dark"
              onClick={onPreview}
            >
              {t("preview_label")}
            </button>
            <button
              className="tutor-session-card__btn tutor-session-card__btn--dimmed"
              disabled
            >
              {startLabel}
            </button>
          </div>
      </div>
    </div>
  );
};

export default TutorSessionCard;
